/**
 * O campo do formulário do SM: rótulo mono em caixa alta e o controle embaixo.
 *
 * Papel: reproduz `.field label`, `.field input/select/textarea` e `.row2` (l.
 * 395–403). O controle ocupa a largura toda, em Inter 12,5 px, com o anel de
 * 3 px no foco; a `textarea` tem 60 px de altura mínima e só cresce na vertical.
 *
 * Como usar: o controle é do formulário, que lhe dá o `id` e a classe
 * `CLASSE_DO_CONTROLE` (ou `CLASSE_DA_AREA_DE_TEXTO`); o `CampoDoFormulario` só
 * desenha o rótulo ligado a ele por `htmlFor`. Um controle sem o mesmo `id` fica
 * sem nome acessível. `LinhaDupla` põe dois campos lado a lado e volta a uma
 * coluna a 560 px ou menos — a única quebra de largura da tela.
 */
import { type ReactNode } from 'react';

/** O `input` e o `select` do formulário. */
export const CLASSE_DO_CONTROLE =
  'w-full rounded-[6px] border border-emb-borda bg-superficie px-2.5 py-2 font-sans text-[12.5px] text-foreground focus:border-foreground focus:ring-3 focus:ring-emb-foco focus:outline-none';

/** A `textarea` do formulário. */
export const CLASSE_DA_AREA_DE_TEXTO = `${CLASSE_DO_CONTROLE} min-h-[60px] resize-y`;

/**
 * Um campo com rótulo.
 *
 * @param rotulo - O texto do rótulo, como a origem o escreve; a caixa alta vem
 *   do CSS.
 * @param id - O `id` do controle que vem em `children`.
 * @param children - O controle.
 * @returns O rótulo e o controle.
 */
export function CampoDoFormulario({
  rotulo,
  id,
  children,
}: {
  rotulo: string;
  id: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1 block font-mono text-[10px] font-medium tracking-[0.5px] text-texto-fraco uppercase"
      >
        {rotulo}
      </label>
      {children}
    </div>
  );
}

/**
 * Dois campos lado a lado.
 *
 * @param children - Os dois `CampoDoFormulario`.
 * @returns A grade de duas colunas iguais.
 */
export function LinhaDupla({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-3 max-[561px]:grid-cols-1">{children}</div>;
}
